/**
 * YearRoundScenarioView.tsx - shared full-assessment view for a year-round
 * strategy. Used by the four /scenarios/year-round/* pages.
 */
import { Link, useNavigate } from 'react-router';
import {
  ArrowLeft, ShieldCheck, AlertTriangle, AlertCircle, CheckCircle2,
  TrendingDown, Layers, Zap, Activity, FileText,
} from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import type { YearRoundStrategy, MonthRegime, StrategyVerdict } from '@/lib/yearRoundStrategies';
import { MODULES } from '@/lib/moduleRegistry';

const REGIME_COLOUR: Record<MonthRegime['regime'], string> = {
  OFF:  'bg-slate-700/60 text-slate-200',
  LOW:  'bg-sky-700/40 text-sky-200',
  TEM:  'bg-emerald-700/40 text-emerald-200',
  FULL: 'bg-rose-700/40 text-rose-200',
};

const VERDICT_COLOUR: Record<StrategyVerdict, string> = {
  recommended:     'border-emerald-500 bg-emerald-900/20 text-emerald-200',
  good:            'border-cyan-500    bg-cyan-900/20    text-cyan-200',
  acceptable:      'border-amber-500   bg-amber-900/20   text-amber-200',
  not_recommended: 'border-rose-500    bg-rose-900/20    text-rose-200',
};

const VERDICT_LABEL: Record<StrategyVerdict, string> = {
  recommended:     'RECOMMENDED',
  good:            'GOOD',
  acceptable:      'ACCEPTABLE',
  not_recommended: 'NOT RECOMMENDED',
};

export default function YearRoundScenarioView({ strategy }: { strategy: YearRoundStrategy }) {
  const navigate = useNavigate();

  return (
    <div className="space-y-4 p-4">
      <PageHeader icon={FileText}
        title={strategy.label}
        subtitle={strategy.tagline}
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`px-3 py-1 rounded-md border-2 text-xs font-bold ${VERDICT_COLOUR[strategy.verdict]}`}>
              {VERDICT_LABEL[strategy.verdict]}
            </span>
            <Link to="/scenarios/year-round"
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-700 bg-slate-800 px-3 py-1.5 text-sm text-slate-200 hover:bg-slate-700">
              <ArrowLeft className="w-4 h-4" /> Compare strategies
            </Link>
          </div>
        }
      />

      {/* Headline KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <KPI label="Overall health"     value={strategy.health.overall.toFixed(0)} unit="/ 100"
          tone={strategy.health.overall >= 85 ? 'good' : strategy.health.overall >= 70 ? 'warn' : 'bad'} />
        <KPI label="Cyano control"      value={strategy.health.cyanoControl.toFixed(0)}  unit="/ 100" />
        <KPI label="Hypolimnion O2"     value={strategy.health.hypolimnionO2.toFixed(0)} unit="/ 100" />
        <KPI label="Raw-water quality"  value={strategy.health.rawWaterQuality.toFixed(0)} unit="/ 100" />
        <KPI label="Compliance margin"  value={strategy.health.complianceMargin.toFixed(0)} unit="/ 100" />
        <KPI label="Annual energy"      value={strategy.annualEnergy_MWh.toFixed(1)} unit="MWh/yr"
          tone={strategy.annualEnergy_MWh < 100 ? 'good' : strategy.annualEnergy_MWh < 250 ? 'warn' : 'bad'} />
      </div>

      {/* Philosophy */}
      <Section title="Operating philosophy" icon={<Layers className="w-4 h-4" />}>
        <p className="text-sm text-slate-300 leading-relaxed">{strategy.philosophy}</p>
      </Section>

      {/* Annual schedule */}
      <Section title="Year-round schedule (Barcombe climatology)" icon={<Activity className="w-4 h-4" />}>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-slate-400 border-b border-slate-700">
              <tr>
                <th className="text-left py-1.5 px-2">Month</th>
                <th className="text-right py-1.5 px-2">T_surf</th>
                <th className="text-right py-1.5 px-2">T_hypo</th>
                <th className="text-right py-1.5 px-2">ΔT</th>
                <th className="text-right py-1.5 px-2">LN</th>
                <th className="text-right py-1.5 px-2">f_cyano</th>
                <th className="text-right py-1.5 px-2">DO_hypo</th>
                <th className="text-center py-1.5 px-2">Regime</th>
                <th className="text-right py-1.5 px-2">Q_air ×</th>
                <th className="text-left py-1.5 px-2">Notes</th>
              </tr>
            </thead>
            <tbody>
              {strategy.schedule.map(m => (
                <tr key={m.month} className="border-b border-slate-800/50">
                  <td className="py-1 px-2 text-slate-200 font-medium">{m.month}</td>
                  <td className="py-1 px-2 text-right text-slate-200">{m.surfT_C} °C</td>
                  <td className="py-1 px-2 text-right text-slate-200">{m.hypoT_C} °C</td>
                  <td className="py-1 px-2 text-right text-slate-200">{m.dT.toFixed(1)} °C</td>
                  <td className="py-1 px-2 text-right text-slate-200">{m.LN.toFixed(2)}</td>
                  <td className="py-1 px-2 text-right text-slate-200">{m.fCyano.toFixed(2)}</td>
                  <td className="py-1 px-2 text-right text-slate-200">{m.DOhypo_mgL.toFixed(1)}</td>
                  <td className="py-1 px-2 text-center">
                    <span className={`px-2 py-0.5 rounded text-[10px] font-semibold ${REGIME_COLOUR[m.regime]}`}>
                      {m.regime}
                    </span>
                  </td>
                  <td className="py-1 px-2 text-right text-slate-200">{m.Qair_factor.toFixed(2)}</td>
                  <td className="py-1 px-2 text-slate-400 text-[10px]">{m.notes}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      {/* Modules engaged */}
      <Section title={`Modules engaged (${strategy.modules.length})`} icon={<Zap className="w-4 h-4" />}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {strategy.modules.map(m => {
            const mod = MODULES.find(x => x.id === m.id);
            return (
              <button key={m.id}
                onClick={() => mod?.path && navigate(mod.path)}
                className="text-left rounded-md border border-slate-700 bg-slate-900/40 hover:bg-slate-800/60 px-3 py-2 transition-colors">
                <div className="text-sm font-semibold text-cyan-200">{mod?.label ?? m.id}</div>
                <div className="text-[11px] text-slate-400 mt-0.5">{m.role}</div>
              </button>
            );
          })}
        </div>
      </Section>

      {/* Pros / Cons */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Section title="Pros" icon={<CheckCircle2 className="w-4 h-4 text-emerald-300" />}>
          <ul className="space-y-1.5 text-sm text-slate-300">
            {strategy.pros.map((p, i) => (
              <li key={i} className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                <span>{p}</span>
              </li>
            ))}
          </ul>
        </Section>
        <Section title="Cons" icon={<AlertCircle className="w-4 h-4 text-amber-300" />}>
          <ul className="space-y-1.5 text-sm text-slate-300">
            {strategy.cons.map((c, i) => (
              <li key={i} className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
                <span>{c}</span>
              </li>
            ))}
          </ul>
        </Section>
      </div>

      {/* Cost notes */}
      <Section title="Energy & cost notes" icon={<TrendingDown className="w-4 h-4" />}>
        <p className="text-sm text-slate-300">{strategy.costNotes}</p>
      </Section>

      {/* UK compliance */}
      <Section title="UK regulatory compliance" icon={<ShieldCheck className="w-4 h-4 text-emerald-300" />}>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
          <ComplianceChip label="WFD Art. 11"          ok={strategy.ukCompliance.wfd} />
          <ComplianceChip label="DWI Reg 4(2)(a)"      ok={strategy.ukCompliance.dwi} />
          <ComplianceChip label="DEFRA cyano"          ok={strategy.ukCompliance.defra} />
          <ComplianceChip label="Reservoirs Act 1975"  ok={strategy.ukCompliance.reservoirsAct} />
          <ComplianceChip label="Ofwat AMP-8 / PR24"   ok={strategy.ukCompliance.ampOfwat} />
        </div>
        <p className="mt-3 text-xs text-slate-400 italic">{strategy.ukCompliance.notes}</p>
      </Section>

      {/* References */}
      <Section title="Scientific references" icon={<FileText className="w-4 h-4" />}>
        <ul className="space-y-1 text-[12px] text-slate-300">
          {strategy.references.map((r, i) => <li key={i}>- {r}</li>)}
        </ul>
      </Section>
    </div>
  );
}

/* ─── small UI helpers ─── */
function Section({ title, icon, children }: { title: string; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <section className="rounded-xl bg-slate-900/40 border border-slate-700/60 p-4">
      <h3 className="flex items-center gap-2 text-sm font-semibold text-cyan-300 mb-3">{icon}{title}</h3>
      {children}
    </section>
  );
}

function KPI({ label, value, unit, tone = 'neutral' }: { label: string; value: string | number; unit?: string; tone?: 'neutral' | 'good' | 'warn' | 'bad'; }) {
  const cls = tone === 'good' ? 'border-emerald-500/40 text-emerald-300'
    : tone === 'warn' ? 'border-amber-500/40 text-amber-300'
    : tone === 'bad'  ? 'border-rose-500/40 text-rose-300'
    : 'border-slate-600 text-slate-200';
  return (
    <div className={`rounded-lg bg-slate-900/50 border ${cls} p-3`}>
      <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-0.5">{label}</div>
      <div className="text-lg font-semibold">{value}{unit && <span className="text-xs text-slate-400 ml-1">{unit}</span>}</div>
    </div>
  );
}

function ComplianceChip({ label, ok }: { label: string; ok: boolean }) {
  return (
    <div className={`rounded-md border px-2 py-1.5 ${ok ? 'border-emerald-700 bg-emerald-900/15 text-emerald-200' : 'border-rose-700 bg-rose-900/15 text-rose-200'}`}>
      <div className="flex items-center gap-1">
        {ok ? <CheckCircle2 className="w-3.5 h-3.5" /> : <AlertCircle className="w-3.5 h-3.5" />}
        <span className="font-semibold">{label}</span>
      </div>
    </div>
  );
}
